import _ from "lodash";
import CacheByUpdatedAt from "utils/cache/CacheByUpdatedAt";
import CacheStorage from "utils/cache/CacheStorage";
import QueryCachedEmployee from "graphql/queries/QueryCachedEmployee";
import store from 'services/redux/store'
import { addDataByGuid } from 'services/redux/data/actions'
import { dataConstants } from 'services/redux/data/constants'
import GraphqlGroupedBuilder from 'utils/request/graphql/groupedBuilder'
import FetchCachedEmployeeDetails from 'utils/fetch/cached/FetchCachedEmployeeDetails'
import HelperDate, { dateFormat } from 'utils/helper-date'

// Currently this class can't be extended with FetchCache
export default class FetchCachedEmployee {

	static employees = {};
	static cacheUpdatedAt = {};
	static isFetching = {};
	static constructing = {};

	static registerTouchedAt = false;
	static buffer = [];
	
	static construct_all = async () => {
		const keys = CacheStorage.getDataKeys();
		const employeeGuids = keys
			.map( key => {
				return key.replace(CacheStorage.cachePrefix, '');
			} )
			.filter( key => {
				return key.startsWith('employee_');
			} )
			.map( key => {
				return key.replace('employee_', '');
			} );

		await Promise.all( employeeGuids.map( employeeGuid => {
			return this.construct(employeeGuid);
		} ) );
		
		this.dispatch();
	}

	static construct = ( employee_guid ) => {
		if( this.constructing[ employee_guid ] ) return this.constructing[ employee_guid ];
		const pending = this.initializeEmployee( employee_guid ).finally( () => {
			delete this.constructing[ employee_guid ];
		});
		this.constructing[ employee_guid ] = pending;
		return pending;
	}

	static initializeEmployee = async ( employee_guid ) => {
		if( employee_guid in this.cacheUpdatedAt ) {
			return;
		}
		
		this.cacheUpdatedAt[ employee_guid ] = {
			identifier: `employee_${employee_guid}`,
			tables: [ `company_employee_${employee_guid}` ],
			onExpire: () => {
				this.reload({
					employee_guids: [employee_guid]
				});
			},
			updated_at_max_age: ( 5 * 60 ), // updated_at needs to be refresh at most each 5 minutes for this table
			lastTouchAge: ( 3 * 24 * 60 * 60 ) // the amount of seconds a cache needs to be 'touched' within, if it's not met the cache will be removed next time the application starts (We set it to 3 days)
		}
		
		// register the settings table and set the max age to 5 minutes
		CacheByUpdatedAt.register( this.cacheUpdatedAt[ employee_guid ] );

		await CacheStorage.getData( this.cacheUpdatedAt[ employee_guid ].identifier ).then( async results => {
			if( results !== null ) {

				if( _.get( this.cacheUpdatedAt[ employee_guid ], 'lastTouchAge', null ) !== null ) {
					if( results.touched_at < HelperDate.date().subtract( this.cacheUpdatedAt[ employee_guid ].lastTouchAge, 'seconds' ).format( dateFormat.DATE_TIME ) ){
						await CacheStorage.removeData( this.cacheUpdatedAt[ employee_guid ].identifier );
						
						delete this.employees[ employee_guid ];
						
						// Do nothing with the code below!
						return;
					}
				}
				
				if( !_.isEmpty( results.data ) ) {
					CacheByUpdatedAt.registerAsUpdates( {
						tables: this.cacheUpdatedAt[ employee_guid ].tables,
						updated_at: results.updated_at
					} );

					this.process( {
						employee: results.data
					});
				}
			}
		});
	}
	
	static registerIsFetching = ( employee_guids, status ) => {
		_.forEach( employee_guids, ( employee_guid ) => {
			this.isFetching[ employee_guid ] = status;
		});
	}
	
	/*
		Note: this function is only used to re-fetch all cached employees at the start
	 */
	static fetchAll = ( { callbackSuccess = null } ) => {
		// compared to other fetchAll caches we actually don't wait for this one to be done, we just want to fetch the latest data 'soon'

		this.registerTouchedAt = false;
		this.fetch( { employee_guids: Object.keys( this.cacheUpdatedAt ) } );
		this.registerTouchedAt = true;

		callbackSuccess();
	}
	
	static fetchEasy = ({ employee_guids, forceReload = false, details = false } = {} ) => {
		const props = {
			employee_guids: employee_guids,
			forceReload: forceReload
		}
		
		if( details ) {
			FetchCachedEmployeeDetails.fetch( props );
		} else {
			FetchCachedEmployee.fetch( props );
		}
	}

	static fetch = async ( { employee_guids, forceReload = false } = {} ) => {
		const touch = this.registerTouchedAt;
		const guids = _.uniq( _.castArray( employee_guids ).filter( guid => {
			return guid != null;
		} ) );
		const missing = guids.filter( guid => {
			return !this.employees[guid];
		} );
		await Promise.all( guids.map( guid => {
			return this.construct(guid);
		} ) );
		if( missing.some( guid => {
			return this.employees[guid];
		} ) ) this.dispatch();

		const expired = await Promise.all( guids.map( async guid => {
			if( forceReload || !this.employees[ guid ] ) return guid;
			const fresh = await CacheByUpdatedAt.sniff({ tables: this.cacheUpdatedAt[ guid ].tables });
			if( fresh ) {
				if( touch ) await CacheStorage.touchData( this.cacheUpdatedAt[ guid ].identifier );
				return null;
			}
			return guid;
		}) );
		return this.reload({ employee_guids: expired.filter( guid => {
			return guid !== null;
		} ) });
	}

	static reload = async ( { employee_guids = [] } = {} ) => {
		employee_guids = _.uniq( _.castArray( employee_guids ).filter( guid => {
			return guid != null;
		} ) );
		await Promise.all( employee_guids.map( guid => {
			return this.construct(guid);
		} ) );
		employee_guids = employee_guids.filter( guid => {
			return !this.isFetching[guid];
		} );
		if( employee_guids.length === 0 ) return;
		// Reserve before adding to the batch, so overlapping calls cannot enqueue duplicates.
		this.registerIsFetching( employee_guids, true );
		try {
			GraphqlGroupedBuilder.addQuery( 'employees', QueryCachedEmployee, {
				variables: {
					guid: employee_guids,
					scanned_in_at: HelperDate.date().format( dateFormat.DATE ) + '_after'
				},
				resultPath: [ 'employees' ],
				callbackStart: () => {
					this.registerIsFetching( employee_guids, true );
				},
				callbackError: () => {
					this.registerIsFetching( employee_guids, false );
				},
				callbackSuccess: results => {
					_.forEach( results, ( employee ) => {
						CacheByUpdatedAt.registerAsUpdates( { tables: this.cacheUpdatedAt[ employee.guid ]?.tables } );
						CacheStorage.storeData( this.cacheUpdatedAt[ employee.guid ]?.identifier, employee );
						this.process( { employee: employee } );
					});
					
					this.dispatch();
					
					this.registerIsFetching( employee_guids, false );
				}
			})
		} catch( error ) {
			this.registerIsFetching( employee_guids, false );
			throw error;
		}
	}

	static process = ( { employee = null } ) => {
		this.employees[ employee.guid ] = employee;
	}
	
	static dispatch = () => {
		store.dispatch( addDataByGuid( dataConstants.EMPLOYEES, this.employees ) );
	}
}
